/**
 * Beräkningslogik för estimat
 * Ren funktionell logik utan side-effects
 */

// Relativ sökväg, inte @shared — Jest känner inte till aliaset
import {
  radpriserMedBaspaket,
  kategorisummorUrRadpriser,
  andelarSomObjekt
} from '../../../../../shared/baspaketFordelning';

/**
 * Beräknar total för ett enskilt item inklusive eventuella subItems
 * @param {Object} item - Estimat-item med möjliga subItems
 * @returns {number} Total summa för item
 */
export const calculateItemTotal = (item) => {
  if (!item || !item.selected) return 0;

  // Summera subItems om de finns
  const subItemsTotal = (item.subItems || []).reduce((sum, sub) => {
    if (sub && sub.selected && sub.quantity > 0) {
      const subPrice = typeof sub.price === 'object' ? (sub.price?.totalt || 0) : (sub.price || 0);
      return sum + (subPrice * sub.quantity);
    }
    return sum;
  }, 0);

  // Om useSubItemsTotal är true, använd ENDAST subItems-summan
  if (item.useSubItemsTotal) {
    return subItemsTotal;
  }

  // Annars: parent-itemets värde + subItems
  const itemPrice = typeof item.price === 'object' ? (item.price?.totalt || 0) : (item.price || 0);
  const parentTotal = itemPrice * (item.quantity || 0);
  return parentTotal + subItemsTotal;
};

/**
 * Beräknar total kostnad för alla valda poster i estimatet
 * @param {Object} estimatData - Estimat-data (formData.estimat)
 * @returns {number} Total summa
 */
export const calculateTotal = (estimatData) => {
  let total = 0;

  if (!estimatData) return total;

  Object.keys(estimatData).forEach(categoryKey => {
    const categoryData = estimatData[categoryKey];
    if (!categoryData || typeof categoryData !== 'object') return;

    Object.keys(categoryData).forEach(itemName => {
      const item = categoryData[itemName];
      total += calculateItemTotal(item);
    });
  });

  return total;
};

/**
 * Beräknar delsummeringar per kategori + totaler för byggoffert (inkl. etablering & ROT)
 * Baspaket (IA-arvode) fördelas proportionellt över alla aktiva kategorier
 * @param {Object} estimatData - Estimat-data (formData.estimat)
 * @param {Object} projectInfo - Projekt-info (formData.projectInfo)
 * @param {boolean} isRotBeloppEditable - Om ROT-belopp är manuellt redigerat
 * @param {Array} laastaAndelar - Estimat.baspaketAndelar; med dem räknas
 *   kategorisummorna ur radpriserna, så översikt och rader stämmer
 * @returns {Object} Resultat med alla delsummor
 */
export const calculateSectionTotals = (estimatData, projectInfo = {}, isRotBeloppEditable = false, laastaAndelar = null) => {
  const result = {
    // Basvärden (utan baspaket)
    projektering: 0,
    byggoffert: 0, // bas (utan etablering/ROT)
    byggoffertEtablering: 0,
    byggoffertROT: 0,
    byggoffertFinal: 0,
    byggoffertRotMax: 0,
    sidoentreprenorer: 0,
    inkopFastInredning: 0,
    vatBase: 0,
    rotAntal: 0,
    rotBelopp: 0,
    inredningsHjalp: 0, // Baspaket (IA-arvode)
    etableringAdminTotal: 0, // Etablering + proportionell andel av baspaket
    
    // Nya fält: Värden inklusive proportionell baspaket-fördelning
    projekteringInklBaspaket: 0,
    byggoffertInklBaspaket: 0,
    etableringInklBaspaket: 0, // Bara etablering + sin andel av baspaket
    sidoentreprenorerInklBaspaket: 0,
    inkopFastInredningInklBaspaket: 0,
    
    // Fördelning av baspaket per kategori (för visning)
    baspaketFordelning: {
      projektering: 0,
      byggoffert: 0,
      etablering: 0,
      sidoentreprenorer: 0,
      inkopFastInredning: 0
    }
  };
  
  if (!estimatData) return result;

  // Projektering, Sidoentreprenörer, Inköp - använd calculateItemTotal för subItems-stöd
  ['projektering', 'sidoentreprenorer', 'inkopFastInredning'].forEach((key) => {
    const entries = estimatData[key] || {};
    Object.values(entries).forEach((it) => {
      result[key] += calculateItemTotal(it);
    });
  });

  // Byggoffert – bas + etablering + ROT - använd calculateItemTotal för subItems-stöd
  const byggoffertEntries = estimatData['byggoffert'] || {};
  let etableringBase = 0;
  Object.entries(byggoffertEntries).forEach(([k, it]) => {
    const itemTotal = calculateItemTotal(it);
    if (itemTotal > 0) {
      result.byggoffert += itemTotal;
      if (!k.includes('Altan') && !k.includes('Ställning')) {
        etableringBase += itemTotal;
      }
    }
  });
  
  const etableringPercentDecimal = Math.max(0, Number(projectInfo?.etableringPercent ?? 10)) / 100;
  result.byggoffertEtablering = etableringBase * etableringPercentDecimal;
  
  // Summera "Hjälp från inredningsarkitekt" (baspaket) - använd calculateItemTotal för subItems-stöd
  const inredningsHelpEntries = estimatData['inredningsarkitekt'] || {};
  let inredningsHelpTotal = 0;
  Object.values(inredningsHelpEntries).forEach((it) => {
    inredningsHelpTotal += calculateItemTotal(it);
  });
  result.inredningsHjalp = inredningsHelpTotal;
  
  // ====================================================================
  // FÖRDELA BASPAKET PROPORTIONELLT ÖVER ALLA AKTIVA KATEGORIER
  // ====================================================================
  
  // Beräkna total summa av alla kategorier (utan baspaket)
  const totalUtanBaspaket = 
    result.projektering + 
    result.byggoffert + 
    result.byggoffertEtablering + 
    result.sidoentreprenorer + 
    result.inkopFastInredning;
  
  // Fördela baspaket proportionellt
  if (totalUtanBaspaket > 0 && inredningsHelpTotal > 0) {
    // Projektering
    const projekteringAndel = result.projektering / totalUtanBaspaket;
    result.baspaketFordelning.projektering = Math.round(inredningsHelpTotal * projekteringAndel);
    result.projekteringInklBaspaket = result.projektering + result.baspaketFordelning.projektering;
    
    // Byggoffert
    const byggoffertAndel = result.byggoffert / totalUtanBaspaket;
    result.baspaketFordelning.byggoffert = Math.round(inredningsHelpTotal * byggoffertAndel);
    result.byggoffertInklBaspaket = result.byggoffert + result.baspaketFordelning.byggoffert;
    
    // Etablering
    const etableringAndel = result.byggoffertEtablering / totalUtanBaspaket;
    result.baspaketFordelning.etablering = Math.round(inredningsHelpTotal * etableringAndel);
    result.etableringInklBaspaket = result.byggoffertEtablering + result.baspaketFordelning.etablering;
    
    // Sidoentreprenörer
    const sidoAndel = result.sidoentreprenorer / totalUtanBaspaket;
    result.baspaketFordelning.sidoentreprenorer = Math.round(inredningsHelpTotal * sidoAndel);
    result.sidoentreprenorerInklBaspaket = result.sidoentreprenorer + result.baspaketFordelning.sidoentreprenorer;
    
    // Inköp fast inredning
    const inkopAndel = result.inkopFastInredning / totalUtanBaspaket;
    result.baspaketFordelning.inkopFastInredning = Math.round(inredningsHelpTotal * inkopAndel);
    result.inkopFastInredningInklBaspaket = result.inkopFastInredning + result.baspaketFordelning.inkopFastInredning;
    
    // Justera avrundningsfel på största kategorin (byggoffert)
    const totalFordelat =
      result.baspaketFordelning.projektering +
      result.baspaketFordelning.byggoffert +
      result.baspaketFordelning.etablering +
      result.baspaketFordelning.sidoentreprenorer +
      result.baspaketFordelning.inkopFastInredning;

    const avrundningsDiff = inredningsHelpTotal - totalFordelat;
    if (avrundningsDiff !== 0) {
      result.baspaketFordelning.byggoffert += avrundningsDiff;
      result.byggoffertInklBaspaket += avrundningsDiff;
    }

    // MAV-runda inkl-baspaket-värdena till närmaste 100-lapp för renare presentation,
    // och justera fördelat-beloppen så att Original + Fördelat = InklBaspaket bibehålls.
    const round100 = (v) => Math.round(v / 100) * 100;
    result.projekteringInklBaspaket = round100(result.projekteringInklBaspaket);
    result.baspaketFordelning.projektering = result.projekteringInklBaspaket - result.projektering;

    result.byggoffertInklBaspaket = round100(result.byggoffertInklBaspaket);
    result.baspaketFordelning.byggoffert = result.byggoffertInklBaspaket - result.byggoffert;

    result.etableringInklBaspaket = round100(result.etableringInklBaspaket);
    result.baspaketFordelning.etablering = result.etableringInklBaspaket - result.byggoffertEtablering;

    result.sidoentreprenorerInklBaspaket = round100(result.sidoentreprenorerInklBaspaket);
    result.baspaketFordelning.sidoentreprenorer = result.sidoentreprenorerInklBaspaket - result.sidoentreprenorer;

    result.inkopFastInredningInklBaspaket = round100(result.inkopFastInredningInklBaspaket);
    result.baspaketFordelning.inkopFastInredning = result.inkopFastInredningInklBaspaket - result.inkopFastInredning;
  } else {
    // Ingen fördelning behövs - sätt inkl-värden till basvärden
    result.projekteringInklBaspaket = result.projektering;
    result.byggoffertInklBaspaket = result.byggoffert;
    result.etableringInklBaspaket = result.byggoffertEtablering;
    result.sidoentreprenorerInklBaspaket = result.sidoentreprenorer;
    result.inkopFastInredningInklBaspaket = result.inkopFastInredning;
  }

  // Låsta andelar: kategorierna är summan av sina rader. Den proportionella
  // fördelningen ovan hade annars gett andra siffror än raderna.
  if (andelarSomObjekt(laastaAndelar) && totalUtanBaspaket > 0 && inredningsHelpTotal > 0) {
    const k = kategorisummorUrRadpriser(estimatData, radpriserMedBaspaket(estimatData, projectInfo, laastaAndelar));
    result.projekteringInklBaspaket = k.projektering;
    result.byggoffertInklBaspaket = k.byggoffert;
    result.etableringInklBaspaket = k.etablering;
    result.sidoentreprenorerInklBaspaket = k.sidoentreprenorer;
    result.inkopFastInredningInklBaspaket = k.inkopFastInredning;
    result.baspaketFordelning = {
      projektering: k.projektering - result.projektering,
      byggoffert: k.byggoffert - result.byggoffert,
      etablering: k.etablering - result.byggoffertEtablering,
      sidoentreprenorer: k.sidoentreprenorer - result.sidoentreprenorer,
      inkopFastInredning: k.inkopFastInredning - result.inkopFastInredning
    };
  }

  // etableringAdminTotal = etablering + sin andel av baspaket (för bakåtkompatibilitet)
  result.etableringAdminTotal = result.etableringInklBaspaket;
  
  const rotAntal = projectInfo?.rotAvdrag || 0;
  const rotBeloppPerPerson = projectInfo?.rotBelopp || 50000;
  result.rotAntal = rotAntal;
  result.rotBelopp = rotBeloppPerPerson;
  
  // rotBelopp är nu alltid belopp PER PERSON - multiplicera med antal
  const requestedRot = rotAntal * rotBeloppPerPerson;
  
  // ROT-maxtak = (byggoffert inkl baspaket + etablering inkl baspaket) * 0.7 * 0.3
  const rotMax = (result.byggoffertInklBaspaket + result.etableringInklBaspaket) * 0.7 * 0.3;
  result.byggoffertRotMax = Math.max(0, rotMax);
  result.byggoffertROT = Math.min(requestedRot, result.byggoffertRotMax);
  result.byggoffertFinal = result.byggoffertInklBaspaket + result.etableringInklBaspaket - result.byggoffertROT;

  // Momsbasen (alla delar inkl. baspaket, innan ROT-avdrag)
  result.vatBase =
    result.projekteringInklBaspaket +
    result.byggoffertInklBaspaket +
    result.etableringInklBaspaket +
    result.sidoentreprenorerInklBaspaket +
    result.inkopFastInredningInklBaspaket;

  return result;
};

/**
 * Radpriser inklusive baspaketets andel, som kunden ser dem.
 * Map med { categoryKey_itemName: pris }.
 *
 * Beräkningen bor i shared/baspaketFordelning.js — backenden låser andelarna
 * med samma kod när en version skickas. Med `laastaAndelar`
 * (Estimat.baspaketAndelar) behåller befintliga rader sitt pris mellan
 * versionerna; utan dem fördelas baspaketet proportionellt som förut.
 *
 * @param {Object} estimatData - estimatKategorier
 * @param {Object} projectInfo - Projekt-info
 * @param {Array} laastaAndelar - Estimat.baspaketAndelar (valfri)
 */
export const calculateItemPricesWithBaspaket = (estimatData, projectInfo = {}, laastaAndelar = null) =>
  radpriserMedBaspaket(estimatData, projectInfo, laastaAndelar);

/**
 * Formaterar ett belopp som SEK
 * @param {number} amount - Belopp
 * @returns {string} Formaterat belopp (t.ex. "125 000 kr")
 */
export const formatAmount = (amount) => {
  return `${Math.round(amount).toLocaleString('sv-SE')} kr`;
};

/**
 * Beräknar procentandel
 * @param {number} part - Del
 * @param {number} total - Total
 * @returns {number} Procent (0-100)
 */
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return 0;
  return (part / total) * 100;
};
